import { useParams } from 'react-router-dom';
import { useGetProfilesQuery } from '../../store/backendApi';
import { EditTripHeader } from './EditTripHeader';
import { EditTripSidebar } from './EditTripSidebar';
import { EditStoryModal } from './story/EditStoryModal';
import { useEditTripData } from '../../hooks/editTrip/useEditTripData';
import { usePublishTrip } from '../../hooks/editTrip/usePublishTrip';
import { useEditStoryModal } from '../../hooks/editTrip/useEditStoryModal';
import { useEditSidebar } from '../../hooks/editTrip/useEditSidebar';
import { useStoryOperations } from '../../hooks/editTrip/useStoryOperations';
import { useTripNavigation } from '../../hooks/editTrip/useTripNavigation';
import { EditTripActions } from './EditTripActions';
import { EditTripSettingsMenu } from './EditTripSettingsMenu';
import { EditStoryList } from './EditStoryList';
import { StoryListSkeleton, TripHeaderSkeleton } from '../../components/skeletons';
import { ErrorBoundary } from '../../components/errorBoundary';
import { Story } from '../../schema';

/**
 * Edit trip page component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
export const EditTripPage = () => {
  const params = useParams();
  const tripId = params['tripId'];
  const { data: user } = useGetProfilesQuery();

  // Business logic hooks (platform-agnostic)
  const { trip, stories, error } = useEditTripData(tripId);
  const { publish, isPublishing, hasUnpublishedChanges } = usePublishTrip(
    tripId,
    trip?.unpublishedChanges,
  );
  const { viewTripUrl } = useTripNavigation(trip, user?.username, tripId);
  const { deleteStory } = useStoryOperations(tripId);

  // UI state hooks
  const storyModal = useEditStoryModal();
  const sidebar = useEditSidebar();

  // Handlers
  const handleAddStory = () => {
    // Just open modal in create mode - no API call yet
    storyModal.openForCreate();
  };

  const handleStoryCreated = (newStory: Story) => {
    // After story is created, switch to edit mode
    storyModal.switchToEditMode(newStory);
  };

  // Error handling
  if (error) {
    return <p>{error}</p>;
  }

  return (
    <div className="relative bg-canvas min-h-screen">
      <div className="relative">
        {/* Show skeleton if trip not loaded */}
        {!trip ? <TripHeaderSkeleton /> : <EditTripHeader tripData={trip} />}

        {/* Only show settings when trip is loaded */}
        {trip && (
          <EditTripSettingsMenu
            onOpenDetails={() => sidebar.open('trip-details')}
            onOpenSharing={() => sidebar.open('privacy-sharing')}
          />
        )}
      </div>

      {/* Only show actions when trip is loaded */}
      {trip && (
        <EditTripActions
          hasUnpublishedChanges={hasUnpublishedChanges}
          isPublishing={isPublishing}
          onPublish={publish}
          viewTripUrl={viewTripUrl}
        />
      )}

      <ErrorBoundary>
        <EditStoryModal
          story={storyModal.editingStory}
          tripId={tripId}
          isOpen={storyModal.isOpen}
          onClose={storyModal.close}
          onStoryCreated={handleStoryCreated}
        />
      </ErrorBoundary>

      {trip && (
        <ErrorBoundary>
          <EditTripSidebar
            tripData={trip}
            section={sidebar.activeSection}
            onClose={sidebar.close}
          />
        </ErrorBoundary>
      )}

      {/* Show skeleton if stories not loaded */}
      {!stories ? (
        <StoryListSkeleton />
      ) : (
        <EditStoryList
          stories={stories}
          isCreatingStory={false}
          onAddStory={handleAddStory}
          onEditStory={storyModal.openForEdit}
          onDeleteStory={deleteStory}
        />
      )}

      <footer className="bg-canvas">
        <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-between lg:px-8">
          <p className="mt-8 text-center text-sm/6 text-muted md:order-1 md:mt-0">
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
